import { addMonths, startOfMonth } from 'date-fns';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import type * as csv from 'fast-csv';
import { jobTicketWrapperFromPerson, type JobTicketSource } from '../../ticket/jobTicketWrapperFromPerson.js';
import { Gender } from '../enums/misc-enums.js';

export const streamToString = async (stream: csv.CsvFormatterStream<csv.FormatterRow, csv.FormatterRow>) => {
  return new Promise<string>(resolve => {
    let str = '';
    stream.on('data', chunk => (str += chunk));
    stream.on('end', () => resolve(str));
  });
};

/**
 * A (Änderung) gültigAb <= dieser Monat && gültigBis > nächster Monat && Änderung innerhalb 10. letzer Monat bis 10. dieser Monat
 * B (Bestellung) GültigAb = nächster Monat
 * R (Rücknahme) GültigBis = nächster Monat && exportiertAm !== null
 */
export const getTicketCSVObject = (
  user: JobTicketSource & { email?: Maybe<string> },
  index: number,
  auftragsart: 'B' | 'A' | 'R' | null,
) => {
  const jobTicket = jobTicketWrapperFromPerson(user);
  const today = todayAsCalendarDay();
  const startOfNextMonth = startOfMonth(addMonths(today, 1));
  const expObj: Record<string, any> = {};
  expObj.ID = index;
  expObj.Auftragsart = auftragsart;
  expObj.Auftragsdatum =
    auftragsart === 'B'
      ? jobTicket.jobTicketBestellDatum
        ? formatToGermanDate(jobTicket.jobTicketBestellDatum)
        : null
      : auftragsart === 'R'
        ? jobTicket.jobTicketKuendigungsDatum
          ? formatToGermanDate(jobTicket.jobTicketKuendigungsDatum)
          : null
        : formatToGermanDate(startOfNextMonth);
  expObj.Sortenschlüssel = auftragsart === 'B' ? null : 1460;
  expObj.Preisstufe = auftragsart === 'B' ? null : 100;
  expObj.VonTG = auftragsart === 'B' ? null : 9900;
  expObj.NachTG = auftragsart === 'B' ? null : 9900;
  expObj.UeberTG = auftragsart === 'B' ? null : 0;
  expObj.SortenschluesselNeu = auftragsart != 'B' ? null : 1460;
  expObj.PreisstufeNeu = auftragsart != 'B' ? null : 100;
  expObj.VonTGNeu = auftragsart != 'B' ? null : 9900;
  expObj.NachTGNeu = auftragsart != 'B' ? null : 9900;
  expObj.UeberTGNeu = auftragsart != 'B' ? null : 0;
  expObj.VonHaltestelleNeu = null;
  expObj.NachHaltestelleNeu = null;
  expObj.UeberHaltestelle = null;

  switch (jobTicket.geschlecht) {
    case Gender.MALE:
      expObj.Anrede = 'Herr';
      break;
    case Gender.FEMALE:
      expObj.Anrede = 'Frau';
      break;
    case Gender.DIVERS:
      expObj.Anrede = 'Divers';
      break;
    default:
      expObj.Anrede = null;
  }

  expObj.Titel = null;

  expObj.Vorname = jobTicket.vorname;
  expObj.Nachname = jobTicket.nachname;

  expObj.Firma = null;
  expObj.Adresszusatz = null;

  const streetAndNumberSeperatorRegex = /(?= \d| [\w\d/]+$)/;
  expObj.Straße = jobTicket.streetAndNumber?.split(streetAndNumberSeperatorRegex)[0]?.trim();
  expObj.Hausnummer = jobTicket.streetAndNumber?.split(streetAndNumberSeperatorRegex)[1]?.trim();
  expObj.PLZ = jobTicket.zip;
  expObj.Ort = jobTicket.city;

  expObj.Ortsteil = null;

  expObj.Geburtsdatum = formatToGermanDate(jobTicket.geburtsDatum);

  expObj.EMail = filterDummyEmail(user.email ?? undefined, '');
  expObj.LandTelefon = null;
  expObj.VorwahlTelefon = null;
  expObj.RufnummerTelefon = null;

  expObj.NutzerID = user.mitarbeiterDaten?.personalNr;

  return expObj;
};

const formatToGermanDate = (date: Maybe<Date>) => {
  if (!date) return null;
  // einstelliges Format wie das bisherige de-DE Kurzdatum, der Abnehmer der CSV erwartet es so
  return formatCalendarDay(date, 'd.M.yyyy');
};

export const E_MAIL_DOMAIN_PLACEHOLDER = 'placeholder.tmp';

/** a generated address standing for "no address" - never written by a person, never worth comparing */
export const isDummyEmail = (email: Maybe<string>): boolean => {
  const normalized = (email ?? '').trim().toLowerCase();
  return normalized.endsWith(`@${E_MAIL_DOMAIN_PLACEHOLDER}`) || normalized.endsWith('@no.mail');
};

export const filterDummyEmail = (email?: string, placeholder: string = '---') => {
  if (!email) return placeholder;
  const dummyCheck = new RegExp(`.*@${E_MAIL_DOMAIN_PLACEHOLDER}`);
  return dummyCheck.test(email) ? placeholder : email;
};
